import type { BingMapCoordinates } from 'ts/types';
import fetchApi from 'utils/api-utils';
import { getProps } from 'utils/global-utils';
import type {
  GetCoordinatesByAddress,
  GetDurationByCoords,
  GetGeocode,
  GetLocation,
  GetLocationByAddress,
  GetPlaceDetails,
  GetSuggestions,
} from './types';

const LOCATIONS_URL = process.env.REACT_APP_LOCATIONS_URL;
const AUTO_SUGGEST_URL = process.env.REACT_APP_AUTO_SUGGEST_URL;
const BING_MAPS_KEY = process.env.REACT_APP_BING_MAPS_KEY;
const ROUTE_DURATION_URL = process.env.REACT_APP_ROUTE_DURATION_URL;
const SYNERGY_GET_QUERY_AUTOCOMPLETE_URL =
  process.env.REACT_APP_SYNERGY_GET_QUERY_AUTOCOMPLETE_URL;
const SYNERGY_GET_PLACE_DETAILS_URL =
  process.env.REACT_APP_SYNERGY_GET_PLACE_DETAILS_URL;
const SYNERGY_GET_LOCATION_BY_COORDINATES_URL =
  process.env.REACT_APP_SYNERGY_GET_LOCATION_BY_COORDINATES_URL;

export const getLocation: GetLocation = async ({
  addressLine,
  countryRegion,
  coords,
}) => {
  const rs = await (addressLine
    ? getLocationByAddress({ addressLine, countryRegion })
    : getLocationByCoords({ coords }));
  const data = getProps(rs, 'json') && (await rs.json());
  const result = addressLine
    ? data?.resourceSets?.[0]?.resources?.[0]?.value.map(
        (resultObject: any) => resultObject.address,
      )
    : [data?.resourceSets?.[0]?.resources?.[0]?.address];
  return { status: rs.status, ok: rs.ok, result: result || [{}] };
};

const getLocationByAddress: GetLocationByAddress = ({
  addressLine,
  countryRegion,
}) =>
  fetchApi(AUTO_SUGGEST_URL, {
    method: 'GET',
    headers: {
      accept: '*/*',
      'sec-fetch-dest': 'empty',
      'sec-fetch-site': 'cross-site',
    },
    params: {
      key: BING_MAPS_KEY,
      query: addressLine,
      countryFilter: countryRegion,
      includeEntityTypes: 'Address',
      maxResults: 3,
    },
  });

const getLocationByCoords = ({ coords }: { coords: BingMapCoordinates }) =>
  fetchApi(`${LOCATIONS_URL}/${coords.latitude},${coords.longitude}`, {
    method: 'GET',
    params: {
      key: BING_MAPS_KEY,
    },
  });

export const getDurationByCoords: GetDurationByCoords = async ({
  origins,
  destinations,
}) => {
  const rs = await fetchApi(ROUTE_DURATION_URL, {
    method: 'POST',
    headers: {
      accept: '*/*',
    },
    params: {
      key: BING_MAPS_KEY,
    },
    body: {
      origins,
      destinations,
      travelMode: 'Driving',
      timeUnit: 'minute',
    },
  });
  const data = getProps(rs, 'json') && (await rs.json());
  return {
    status: rs.status,
    ok: rs.ok,
    result: data.resourceSets[0].resources[0].results[0],
  };
};

export const getCoordinatesByAddress: GetCoordinatesByAddress = async ({
  formattedAddress,
}) => {
  const encodedQuery = encodeURI(formattedAddress);
  const rs = await fetchApi(`${LOCATIONS_URL}/${encodedQuery}`, {
    method: 'GET',
    params: {
      key: BING_MAPS_KEY,
    },
  });
  const data = getProps(rs, 'json') && (await rs.json());

  return {
    status: rs.status,
    ok: rs.ok,
    result: data,
  };
};

/**
 * Using Google Map's API
 */
export const getSuggestions: GetSuggestions = async ({
  input,
  language,
  components,
}) => {
  const rs = await fetchApi(SYNERGY_GET_QUERY_AUTOCOMPLETE_URL, {
    method: 'GET',
    params: {
      input,
      language,
      components,
    },
  });

  const data = getProps(rs, 'json') && (await rs.json());
  return {
    status: rs.status,
    ok: rs.ok,
    result: data,
  };
};

/**
 * Used in conjunction with `getSuggestions` api function
 */
export const getPlaceDetails: GetPlaceDetails = async ({
  placeId,
  language,
}) => {
  const rs = await fetchApi(SYNERGY_GET_PLACE_DETAILS_URL, {
    method: 'GET',
    params: {
      language,
      placeId,
    },
  });

  const data = getProps(rs, 'json') && (await rs.json());
  return {
    status: rs.status,
    ok: rs.ok,
    result: data,
  };
};

/**
 * Using Google API
 */
export const getGeocode: GetGeocode = async ({
  latitude,
  longitude,
  language,
}) => {
  const rs = await fetchApi(SYNERGY_GET_LOCATION_BY_COORDINATES_URL, {
    method: 'GET',
    params: {
      language,
      latitude,
      longitude,
    },
  });

  const data = getProps(rs, 'json') && (await rs.json());
  return {
    status: rs.status,
    ok: rs.ok,
    result: data,
  };
};

export default getLocation;
